import { LOCALE_CODES, LOCALE_META } from '@refego/core/locales'
import { useQueryClient } from '@tanstack/react-query'
import { Link, Outlet, useNavigate } from '@tanstack/react-router'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useT } from '../lib/i18n'
import { currentWorkspace, type Me, setWorkspace, signOut } from '../lib/session'
import { Logo } from './ui'

/** Language menu: every supported locale by its native name; untranslated texts fall back to English. */
function LangMenu({ up = false }: { up?: boolean }) {
  const { locale, setLocale } = useT()
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const away = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false)
    }
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setOpen(false)
      root.current?.querySelector<HTMLButtonElement>('.lang-toggle')?.focus()
    }
    document.addEventListener('pointerdown', away)
    document.addEventListener('keydown', esc)
    root.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus()
    return () => {
      document.removeEventListener('pointerdown', away)
      document.removeEventListener('keydown', esc)
    }
  }, [open])
  return (
    <div className="lang" ref={root}>
      <button
        type="button"
        className="lang-toggle"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Language: ${LOCALE_META[locale].name}`}
        onClick={() => setOpen(!open)}
      >
        <i className="lang-dot" aria-hidden="true" />
        <span className="uppercase">{locale}</span>
        <svg className="lang-chev" width="12" height="12" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M6 9l6 6 6-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div className={`lang-menu ${up ? 'lang-menu-up' : ''}`} role="menu" aria-label="Language">
          {LOCALE_CODES.map((l) => (
            <button
              key={l}
              type="button"
              role="menuitemradio"
              aria-checked={l === locale}
              lang={l}
              className="lang-item"
              onClick={() => {
                setOpen(false)
                if (l !== locale) setLocale(l)
              }}
            >
              <span className="lang-item-code">{l}</span>
              {LOCALE_META[l].name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const ICON: Record<string, ReactNode> = {
  overview: <path d="M4 13h6V4H4zM14 20h6V11h-6zM4 20h6v-3H4zM14 7h6V4h-6z" />,
  partners: (
    <path d="M16 19v-1a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v1M9.5 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM21 19v-1a4 4 0 0 0-3-3.9M16 4.1a3 3 0 0 1 0 5.8" />
  ),
  commissions: <path d="M4 7h16M4 12h16M4 17h10" />,
  payouts: <path d="M3 7h18v10H3zM7 12h.01M17 12h.01M12 14.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z" />,
  fraud: <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6zM12 8v4M12 16h.01" />,
  settings: (
    <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
  ),
  earnings: <path d="M12 2v20M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />,
}

const Icon = ({ name }: { name: string }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {ICON[name]}
  </svg>
)

export function Shell({
  me,
  area,
  nav,
}: {
  me: Me
  area: 'owner' | 'partner'
  nav: { to: string; label: string; icon: string; exact?: boolean }[]
}) {
  const { t } = useT()
  const [menu, setMenu] = useState(false)
  const qc = useQueryClient()
  const navigate = useNavigate()
  const ws = currentWorkspace(me)
  return (
    <div className="min-h-screen md:grid md:grid-cols-[240px_1fr]">
      <aside
        className={`${menu ? 'block' : 'hidden'} border-b border-hairline bg-canvas md:sticky md:top-0 md:block md:h-screen md:border-b-0 md:border-r`}
      >
        <div className="flex h-full flex-col gap-1 p-4">
          <div className="mb-6 hidden px-2 pt-2 md:block">
            <Logo />
          </div>
          {area === 'owner' && me.memberships.length > 1 && (
            <select
              className="select mb-4 text-sm"
              aria-label={t('pickWorkspace')}
              value={ws ?? ''}
              onChange={(e) => {
                setWorkspace(e.target.value)
                qc.invalidateQueries()
              }}
            >
              {me.memberships.map((m) => (
                <option key={m.workspaceId} value={m.workspaceId}>
                  {m.name}
                </option>
              ))}
            </select>
          )}
          <div className="eyebrow mb-2 px-3">
            {area === 'owner' ? t('ownerDashboard') : t('partnerDashboard')}
          </div>
          {nav.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className="nav-item"
              activeOptions={{ exact: n.exact ?? false }}
              onClick={() => setMenu(false)}
            >
              <Icon name={n.icon} />
              {n.label}
            </Link>
          ))}
          <div className="mt-auto flex flex-col gap-1 border-t border-hairline pt-4">
            {area === 'owner' && me.partner && (
              <Link to="/partner" className="nav-item">
                ↗ {t('partnerDashboard')}
              </Link>
            )}
            {area === 'partner' && me.memberships.length > 0 && (
              <Link to="/" className="nav-item">
                ↗ {t('ownerDashboard')}
              </Link>
            )}
            {area === 'owner' && (
              <button
                type="button"
                className="nav-item text-left"
                onClick={() => navigate({ to: '/onboarding' })}
              >
                ＋ {t('createWorkspace')}
              </button>
            )}
            <div className="truncate px-3 py-2 text-xs text-mute">{me.user?.email}</div>
            <div className="flex items-center justify-between px-1">
              <LangMenu up />
              <button type="button" className="btn btn-sm btn-ghost" onClick={signOut}>
                {t('signOut')}
              </button>
            </div>
          </div>
        </div>
      </aside>
      <div className="min-w-0">
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-hairline bg-canvas/90 px-4 backdrop-blur md:hidden">
          <Logo />
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setMenu(!menu)}
            aria-expanded={menu}
            aria-label="Menu"
          >
            ☰
          </button>
        </header>
        <main className="mx-auto max-w-[1200px] px-4 py-8 md:px-10 md:py-12">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export function PublicFrame({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen">
      <header className="flex h-16 items-center justify-between border-b border-hairline px-4 md:px-12">
        <a href="https://refego.com" className="text-ink no-underline">
          <Logo />
        </a>
        <LangMenu />
      </header>
      {children}
    </div>
  )
}
